import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React, { forwardRef, useMemo, useState } from 'react';
import { IoMdInformationCircleOutline } from 'react-icons/io';

import { ArticleList } from './ArticleList';
import { useArticles } from '@ais-ui/api';
import { useLocalization } from '@ais-ui/locale-management';
import { filterArticles } from '@ais-ui/utils';
import { useHelp } from './HelpProvider';
import { ErrorDisplay } from '../../../../apps/ais/src/components/ErrorDisplay';
type HelpProps = React.HTMLProps<HTMLDivElement>;

export const Help = forwardRef<HTMLDivElement, HelpProps>((_props, ref) => {
  const { messages } = useLocalization();
  const [search, setSearch] = useState<string>('');
  const { open, setOpen } = useHelp();

  const { data, error } = useArticles();

  const filteredArticles = useMemo(() => {
    if (!data) {
      return [];
    }

    if (search === '') {
      return data;
    }

    return filterArticles(data, search);
  }, [data, search]);

  if (error) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-full max-w-[calc(100%-2rem)] sm:max-w-[650px] lg:max-w-[750px] xl:max-w-[1000px] p-4 lg:p-6 xl:p-10 flex flex-col max-h-[75%] bg-gradient-to-b from-background to-slate-100">
          <ErrorDisplay message="Unable to load help articles. Please try again later.">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </ErrorDisplay>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="w-full max-w-[calc(100%-2rem)] sm:max-w-[1000px] p-10 h-[75%] bg-gradient-to-b from-background to-slate-100 border-0 border-t-4 border-primary">
        <DialogHeader className="flex">
          <DialogTitle className="text-3xl mb-2">
            {messages.helpTitle}
          </DialogTitle>
          <DialogDescription className="text-md">
            {messages.helpSubtitle}
          </DialogDescription>
          <div className="flex">
            <Input
              id="search-help"
              placeholder={messages.helpSearchPlaceholder}
              className="w-1/3 min-w-[250px] focus-visible:ring-0 mt-6"
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
            />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger className="pl-4 flex items-center mt-6">
                  <IoMdInformationCircleOutline className="text-primary" />
                </TooltipTrigger>
                <TooltipContent side="right">
                  {messages.helpSearchTooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </DialogHeader>
        <ArticleList articles={filteredArticles}></ArticleList>
      </DialogContent>
    </Dialog>
  );
});

// Add display name
Help.displayName = 'Help';
